/** Badges and pickers the facilities pages share. Pickers read the help desk from the page, so they list facilities data. */
import { useAssignees, useDepartments, indentedName } from '../lib/automation';
import {
  CONDITION_LABEL,
  CONDITION_TONE,
  EQUIPMENT_STATUS_LABEL,
  EQUIPMENT_STATUS_TONE,
  equipmentLabel,
  INSPECTION_STATUS_LABEL,
  INSPECTION_STATUS_TONE,
  SCHEDULE_STATE_LABEL,
  SCHEDULE_STATE_TONE,
  scoreTone,
  useEquipmentOptions,
  useFacilityPlaces,
  type EquipmentStatus,
  type FacilityCondition,
  type InspectionStatus,
  type ScheduleState,
} from '../lib/facilities';
import { Badge, Field, Select } from './ui';

export function ConditionBadge({ condition }: { condition: FacilityCondition }) {
  return <Badge tone={CONDITION_TONE[condition]}>{CONDITION_LABEL[condition]}</Badge>;
}

export function EquipmentStatusBadge({ status }: { status: EquipmentStatus }) {
  return <Badge tone={EQUIPMENT_STATUS_TONE[status]}>{EQUIPMENT_STATUS_LABEL[status]}</Badge>;
}

export function InspectionStatusBadge({ status, overdue = false }: { status: InspectionStatus; overdue?: boolean }) {
  if (overdue) return <Badge tone="red">Overdue</Badge>;
  return <Badge tone={INSPECTION_STATUS_TONE[status]}>{INSPECTION_STATUS_LABEL[status]}</Badge>;
}

export function ScheduleStateBadge({ state }: { state: ScheduleState }) {
  return <Badge tone={SCHEDULE_STATE_TONE[state]}>{SCHEDULE_STATE_LABEL[state]}</Badge>;
}

export function ScoreBadge({ score }: { score: number | null }) {
  return <Badge tone={scoreTone(score)}>{score === null ? 'No score' : `${score}%`}</Badge>;
}

/** Building and room selects, from the buildings and rooms open today. */
export function PlaceFields({
  buildingId,
  roomId,
  onChange,
  requireBuilding = false,
  emptyBuilding = 'No building',
  disabled = false,
}: {
  buildingId: string;
  roomId: string;
  onChange(next: { buildingId: string; roomId: string }): void;
  requireBuilding?: boolean;
  /** The blank option's label, e.g. "Any building" in a filter. */
  emptyBuilding?: string;
  disabled?: boolean;
}) {
  const places = useFacilityPlaces();
  const rooms = places.data?.find((building) => building.id === buildingId)?.rooms ?? [];
  return (
    <>
      <Field label="Building" required={requireBuilding}>
        <Select value={buildingId} disabled={disabled} onChange={(event) => onChange({ buildingId: event.target.value, roomId: '' })}>
          <option value="">{requireBuilding ? 'Choose a building' : emptyBuilding}</option>
          {places.data?.map((building) => (
            <option key={building.id} value={building.id}>
              {building.name}
            </option>
          ))}
          {/* A saved building that has since closed stays selected instead of silently clearing. */}
          {buildingId && places.data && !places.data.some((building) => building.id === buildingId) && <option value={buildingId}>A closed building</option>}
        </Select>
      </Field>
      <Field label="Room">
        <Select value={roomId} disabled={disabled || !buildingId} onChange={(event) => onChange({ buildingId, roomId: event.target.value })}>
          <option value="">{buildingId ? 'No particular room' : 'Choose a building first'}</option>
          {rooms.map((room) => (
            <option key={room.id} value={room.id}>
              {room.name}
            </option>
          ))}
          {roomId && places.data && !rooms.some((room) => room.id === roomId) && <option value={roomId}>A closed room</option>}
        </Select>
      </Field>
    </>
  );
}

/** Facilities equipment, optionally only in one building. */
export function EquipmentSelect({
  value,
  onChange,
  buildingId,
  emptyLabel = 'No equipment',
  disabled = false,
}: {
  value: string;
  onChange(id: string): void;
  buildingId?: string;
  emptyLabel?: string;
  disabled?: boolean;
}) {
  const options = useEquipmentOptions(buildingId);
  const items = options.data ?? [];
  return (
    <Select value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
      <option value="">{emptyLabel}</option>
      {items.map((equipment) => (
        <option key={equipment.id} value={equipment.id}>
          {equipmentLabel(equipment)}
        </option>
      ))}
      {value && !items.some((equipment) => equipment.id === value) && <option value={value}>Selected equipment</option>}
    </Select>
  );
}

/** The page's help desk departments; facilities categories on facilities pages. */
export function CategorySelect({
  value,
  onChange,
  emptyLabel = 'No category',
  disabled = false,
}: {
  value: string;
  onChange(id: string): void;
  emptyLabel?: string;
  disabled?: boolean;
}) {
  const departments = useDepartments();
  return (
    <Select value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
      <option value="">{emptyLabel}</option>
      {departments.data?.map((department) => (
        <option key={department.id} value={department.id}>
          {indentedName(department)}
        </option>
      ))}
    </Select>
  );
}

/** People who can be given work on the page's help desk. */
export function AssigneeSelect({
  value,
  onChange,
  emptyLabel = 'Nobody',
  disabled = false,
  enabled = true,
}: {
  value: string;
  onChange(id: string): void;
  emptyLabel?: string;
  disabled?: boolean;
  enabled?: boolean;
}) {
  const assignees = useAssignees(enabled);
  const items = assignees.data ?? [];
  return (
    <Select value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
      <option value="">{emptyLabel}</option>
      {items.map((person) => (
        <option key={person.id} value={person.id}>
          {person.name}
        </option>
      ))}
      {value && !items.some((person) => person.id === value) && <option value={value}>Someone else</option>}
    </Select>
  );
}
